import type {ShallowRef} from 'vue';
import {onMounted, onUnmounted, shallowRef} from 'vue';

export type HeroSequence = {
    isVideoFadingOut: ShallowRef<boolean>;
    isUiSequencePlaying: ShallowRef<boolean>;
};

export const HERO_VIDEO_FADE_OUT_DURATION_IN_SECONDS = 0.5;

// The camera of the video settles at this timestamp, the UI composition is choreographed from there.
export const HERO_UI_SEQUENCE_START_IN_SECONDS = 1.75;

export function useHeroSequence(video: Readonly<ShallowRef<HTMLVideoElement | null>>): HeroSequence {
    const isVideoFadingOut = shallowRef(false);
    const isUiSequencePlaying = shallowRef(false);

    let frameId: number | null = null;

    // `timeupdate` only fires about four times per second, which is too coarse to start the composition on an
    // exact timestamp, so the video clock is polled on every frame instead.
    function syncOnVideoTime() {
        const element = video.value;

        if (element && Number.isFinite(element.duration)) {
            // The loop restarts currentTime at 0, which turns the fade-out back into a fade-in and rewinds the
            // UI sequence to its initial state until the next start mark.
            isVideoFadingOut.value = element.duration - element.currentTime <= HERO_VIDEO_FADE_OUT_DURATION_IN_SECONDS;
            isUiSequencePlaying.value = element.currentTime >= HERO_UI_SEQUENCE_START_IN_SECONDS;
        }

        frameId = window.requestAnimationFrame(syncOnVideoTime);
    }

    onMounted(async () => {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (prefersReducedMotion) {
            pauseVideo(video.value);
            return;
        }

        const isVideoPlaying = await startVideo(video.value);

        if (isVideoPlaying) {
            frameId = window.requestAnimationFrame(syncOnVideoTime);
            return;
        }

        // Without a video clock to follow, the composition plays once on its own.
        isUiSequencePlaying.value = true;
    });

    onUnmounted(() => {
        if (frameId !== null) {
            window.cancelAnimationFrame(frameId);
        }
    });

    return {isVideoFadingOut, isUiSequencePlaying};
}

function pauseVideo(video: HTMLVideoElement | null) {
    video?.pause();
}

async function startVideo(video: HTMLVideoElement | null): Promise<boolean> {
    if (!video) {
        return false;
    }

    // Autoplay is only reliable when these are set as properties, the template attributes alone are not enough.
    video.muted = true;
    video.loop = true;
    video.playsInline = true;

    try {
        await video.play();
        return true;
    } catch {
        // Autoplay can be refused by the browser, the first frame stays displayed.
        return false;
    }
}
